import { inject, Injectable } from '@angular/core';
import { ApiClientService, ApiError, ApiErrorResponse } from '../api-client/api-client.service';
import { CaptureJobInvoice } from '../api-client/model/captureJobInvoice';
import { CaptureData } from '../api-client/model/captureData';
import { ErrorContent } from '../api-client/model/errorContent';

export class InvoiceCaptureError extends Error {
  private readonly data: ErrorContent;
  constructor(data: ApiErrorResponse) {
    super(`${data.error.title}\n${data.error.details}`);
    this.data = data.error;
  }

  get title() {
    return this.data.title;
  }

  get details() {
    return this.data.details;
  }

  get extra() {
    return this.data.extra_data;
  }
}

@Injectable({
  providedIn: 'root',
})
export class InvoiceCaptureService {
  private readonly apiClient = inject(ApiClientService);

  async getJob(): Promise<CaptureJobInvoice | null> {
    try {
      let response = await this.apiClient.getResumeJob();
      if (response === null) {
        response = await this.apiClient.postStartJob();
      }

      if (response === null) {
        return null;
      }

      return response.data;
    } catch (error) {
      const errorData = (error as ApiError).data;
      throw new InvoiceCaptureError(errorData);
    }
  }

  async saveCaptureData(jobId: number, captureData: CaptureData): Promise<void> {
    try {
      await this.apiClient.postCaptureJob(jobId, captureData);
    } catch (error) {
      const errorData = (error as ApiError).data;
      throw new InvoiceCaptureError(errorData);
    }
  }

  async completeJob(jobId: number, captureData: CaptureData): Promise<void> {
    try {
      await this.apiClient.postCompleteJob(jobId, captureData);
    } catch (error) {
      const errorData = (error as ApiError).data;
      throw new InvoiceCaptureError(errorData);
    }
  }

  async getJobsByStatus(statuses: string[]): Promise<CaptureJobInvoice[]> {
    try {
      const response = await this.apiClient.getJobsByStatus(statuses);
      return response.data;
    } catch (error) {
      const errorData = (error as ApiError).data;
      throw new InvoiceCaptureError(errorData);
    }
  }
}
